import { useEffect, useRef, useCallback } from 'react';
import { get } from 'lodash';
import { isBrowser } from '@zapier/common-utils';
import { CrossBrowserDocument, TrackPropsType } from '../types';

interface InitialValue {
  hidden: string;
  visibilityChange: string;
  totalActiveMillis: number;
  totalAbsentMillis: number;
  lastEventTime: number;
  userState: 'active' | 'inactive';
}

const getVisibilityChangeProps = () => {
  if (typeof document.hidden !== 'undefined') {
    // This covers most cases
    return {
      hidden: 'hidden',
      visibilityChange: 'visibilitychange',
    };
  } else if (
    typeof (document as CrossBrowserDocument).msHidden !== 'undefined'
  ) {
    // Internet Explorer and Microsoft Edge
    return {
      hidden: 'msHidden',
      visibilityChange: 'msvisibilitychange',
    };
  } else if (
    typeof (document as CrossBrowserDocument).webkitHidden !== 'undefined'
  ) {
    // Older versions of webkit browsers (Chrome, Safari, newer versions of Opera, almost all iOS browsers (including Firefox for iOS))
    return {
      hidden: 'webkitHidden',
      visibilityChange: 'webkitvisibilitychange',
    };
  }
  return undefined;
};

const initialValue: InitialValue = {
  hidden: '',
  visibilityChange: '',
  totalActiveMillis: 0,
  totalAbsentMillis: 0,
  lastEventTime: Date.now(),
  userState: 'active',
};

function useTrackTime(
  { track, trackingMessage, trackingProps }: TrackPropsType = {
    track: () => {},
  }
) {
  const timeRef = useRef(initialValue);

  function getTimeDelta() {
    const { lastEventTime } = timeRef.current;

    const now = Date.now();
    return {
      now,
      elapsed: now - lastEventTime,
    };
  }

  function setUserActive() {
    const { elapsed, now } = getTimeDelta();
    const { totalAbsentMillis, userState } = timeRef.current;

    if (userState === 'inactive') {
      timeRef.current = {
        ...timeRef.current,
        totalAbsentMillis: totalAbsentMillis + elapsed,
        lastEventTime: now,
        userState: 'active',
      };
    }
  }

  function setUserInactive() {
    const { elapsed, now } = getTimeDelta();

    const { totalActiveMillis, userState } = timeRef.current;

    if (userState === 'active') {
      timeRef.current = {
        ...timeRef.current,
        totalActiveMillis: totalActiveMillis + elapsed,
        lastEventTime: now,
        userState: 'inactive',
      };
    }
  }

  function onComponentUnmount() {
    const { elapsed } = getTimeDelta();
    const message = trackingMessage || 'track time';

    const { totalActiveMillis, totalAbsentMillis, userState } = timeRef.current;

    track(message, {
      ...(trackingProps || {}),
      totalActiveTime:
        totalActiveMillis + (userState === 'active' ? elapsed : 0),
      totalAbsentTime:
        totalAbsentMillis + (userState === 'inactive' ? elapsed : 0),
    });
    window.removeEventListener('beforeunload', onComponentUnmount);
    document.removeEventListener('mouseenter', setUserActive);
    document.removeEventListener('mouseleave', setUserInactive);
  }

  const onComponentUnmountCallback = useCallback(onComponentUnmount, []);

  function handleVisibilityChange() {
    const { hidden } = timeRef.current;

    if (get(document, [hidden])) {
      setUserInactive();
    } else {
      setUserActive();
    }
  }

  function beginEventListening() {
    const { visibilityChange } = timeRef.current;

    document.addEventListener(visibilityChange, handleVisibilityChange);
    window.addEventListener('beforeunload', onComponentUnmount);
    document.addEventListener('mouseenter', setUserActive);
    document.addEventListener('mouseleave', setUserInactive);
    setUserActive();
  }

  const beginEventListeningCallback = useCallback(beginEventListening, []);

  useEffect(() => {
    if (isBrowser()) {
      const visibilityChangeProps = getVisibilityChangeProps();
      if (visibilityChangeProps) {
        timeRef.current = {
          ...timeRef.current,
          ...visibilityChangeProps,
        };

        beginEventListeningCallback();
      }
    }

    // will be called on component unmount
    return () => {
      if (isBrowser()) {
        onComponentUnmountCallback();
      }
    };

    // We only want to run this effect after component mounts, not when beginEventListening or onComponentUnmount changes
  }, [beginEventListeningCallback, onComponentUnmountCallback]);
}

export default useTrackTime;
